/**
 * One batch after the first, in merge order: the next server page, or a fresh
 * copy of an already loaded page that brings back rows which slid onto it
 * after edits removed rows above them.
 */
export interface LoadMoreSlot {
    type: "page" | "gap";
    pageNumber: number;
}

interface AppendNextSlotsParams {
    slots: LoadMoreSlot[];
    /** Edits since the last batch was asked for that may have removed rows. */
    shiftCount: number;
    /** The part of `shiftCount` made while that batch was still loading. */
    inFlightShiftCount: number;
    pageSize: number;
}

/**
 * Asks for the page after the last loaded one. After `shiftCount` removed
 * rows, the same number of unseen rows now close the trailing loaded pages,
 * so those pages are refetched first, in order. A batch that was loading
 * during a shift may have been served before or after it, so the page before
 * that batch is refetched too.
 */
export function appendNextSlots({
    slots,
    shiftCount,
    inFlightShiftCount,
    pageSize,
}: AppendNextSlotsParams): LoadMoreSlot[] {
    const lastPageNumber = slots.reduce(
        (last, slot) => (slot.type === "page" ? slot.pageNumber : last),
        1,
    );
    const inFlightPageCount = inFlightShiftCount > 0 ? 1 : 0;
    const gapPageCount = Math.min(
        lastPageNumber,
        Math.ceil(shiftCount / pageSize) + inFlightPageCount,
    );
    const gapSlots = Array.from(
        { length: gapPageCount },
        (_, index): LoadMoreSlot => ({
            type: "gap",
            pageNumber: lastPageNumber - gapPageCount + index + 1,
        }),
    );
    return [
        ...slots,
        ...gapSlots,
        { type: "page", pageNumber: lastPageNumber + 1 },
    ];
}

interface MergeLoadedPagesParams<TRow, TId> {
    /** Rows per batch in page order; `undefined` for a batch not loaded yet. */
    pages: Array<TRow[] | undefined>;
    getRowId: (row: TRow) => TId;
    pageSize: number;
    total?: number;
    /** Edits since the last batch was asked for that may have removed rows. */
    shiftCount?: number;
}

interface MergedPages<TRow> {
    rows: TRow[];
    hasMore: boolean;
}

/**
 * Joins loaded batches in page order, stopping at the first batch that has not
 * arrived, and keeps only the first copy of a row that shifted into a later
 * batch. With a known total, more rows exist while fewer are loaded and the
 * last batch was not empty; without one, while the last batch came back full
 * or was full before `shiftCount` edits removed rows from it.
 */
export function mergeLoadedPages<TRow, TId>({
    pages,
    getRowId,
    pageSize,
    total,
    shiftCount = 0,
}: MergeLoadedPagesParams<TRow, TId>): MergedPages<TRow> {
    const seenIds = new Set<TId>();
    const rows: TRow[] = [];
    let lastLoadedPage: TRow[] | undefined;

    for (const page of pages) {
        if (!page) {
            break;
        }
        lastLoadedPage = page;
        for (const row of page) {
            const id = getRowId(row);
            if (!seenIds.has(id)) {
                seenIds.add(id);
                rows.push(row);
            }
        }
    }

    return {
        rows,
        hasMore: getHasMore({
            lastLoadedPage,
            rows,
            pageSize,
            total,
            shiftCount,
        }),
    };
}

function getHasMore<TRow>({
    lastLoadedPage,
    rows,
    pageSize,
    total,
    shiftCount,
}: {
    lastLoadedPage: TRow[] | undefined;
    rows: TRow[];
    pageSize: number;
    total?: number;
    shiftCount: number;
}): boolean {
    if (lastLoadedPage === undefined) {
        return false;
    }
    if (total === undefined) {
        return lastLoadedPage.length + shiftCount >= pageSize;
    }
    // A batch shrinks when the table hides a row, so only an empty batch
    // proves an out-of-date total is too high.
    return lastLoadedPage.length > 0 && rows.length < total;
}
